// Mirrored from cloud/functions/src/domain/faults.ts.
// Single source of truth lives there; keep these in sync until both
// codebases consume a shared @ufh/domain workspace package.

// Canonical fault-kind vocabulary. The firmware sets fault_kind on
// every fault-raised / fault-cleared event; cloud-side handlers
// classify severity from this single source.

import { z } from 'zod';

export const FaultKind = z.enum([
  // Tier A sensor faults (immediate)
  'sensor.tier-a.out-of-envelope',
  'sensor.tier-a.open-circuit',
  'sensor.tier-a.short-circuit',
  // Tier B sensor faults (temporal)
  'sensor.tier-b.rate-of-change',
  'sensor.tier-b.stuck',
  // Actuator
  'actuator.stuck',
  // WPM
  'wpm.dewpoint-alarm',
  'wpm.link-lost',
  'wpm.alarm-raised',           // FR-013b — any non-zero ACTIVE ERROR
  // Controller
  'controller.heartbeat-lost',
  'config.stale',
]);
export type FaultKind = z.infer<typeof FaultKind>;

export const FaultSeverity = z.enum(['info', 'warning', 'critical']);
export type FaultSeverity = z.infer<typeof FaultSeverity>;

// Default severity per fault kind. Cloud-side alerts/evaluate.ts can
// override per-fault if operator preferences ever land.
export const FAULT_SEVERITY: Record<FaultKind, FaultSeverity> = {
  'sensor.tier-a.out-of-envelope': 'critical',
  'sensor.tier-a.open-circuit':    'critical',
  'sensor.tier-a.short-circuit':   'critical',
  'sensor.tier-b.rate-of-change':  'warning',
  'sensor.tier-b.stuck':           'warning',
  'actuator.stuck':                'critical',
  'wpm.dewpoint-alarm':            'warning',
  'wpm.link-lost':                 'warning',
  'wpm.alarm-raised':              'warning',
  'controller.heartbeat-lost':     'critical',
  'config.stale':                  'info',
};

// Operator-facing label per fault kind. The raw kind string is still
// shown alongside (operators occasionally need the exact vocabulary), but
// this is what reads in the UI. `Record<FaultKind, string>` means tsc
// fails the build if a new kind is added without a label here.
export const FAULT_LABEL: Record<FaultKind, string> = {
  'sensor.tier-a.out-of-envelope': 'Sensor reading out of range',
  'sensor.tier-a.open-circuit':    'Sensor open circuit',
  'sensor.tier-a.short-circuit':   'Sensor short circuit',
  'sensor.tier-b.rate-of-change':  'Sensor changing too fast',
  'sensor.tier-b.stuck':           'Sensor reading stuck',
  'actuator.stuck':                'Actuator stuck',
  'wpm.dewpoint-alarm':            'Heat pump dew-point alarm',
  'wpm.link-lost':                 'Heat pump link lost',
  'wpm.alarm-raised':              'Heat pump alarm',
  'controller.heartbeat-lost':     'Controller heartbeat lost',
  'config.stale':                  'Configuration stale',
};

// Human-readable label for a fault kind, falling back to the raw string
// for any kind the UI doesn't know about (forward-compatible).
export function faultLabel(kind: string): string {
  return (FAULT_LABEL as Record<string, string>)[kind] ?? kind;
}
